/**
 * The travel / Faye-premium split the Analytics page applies to every figure
 * (rules in server/insurance-finance.js):
 *
 *   - Booked value, GMV, base fares, fees, margin, backer and investor cost,
 *     defaults and recoveries run on TRAVEL rows (`travelFlights`): a premium
 *     rides inside a booking's totals but is collected for Faye.
 *   - Cash-collected metrics (collected %, fee revenue struck on cash,
 *     recovery, cash kept on cancellations) use `travelCashCentsOf`, which
 *     strips the premium's share of the cash.
 *   - Processing is charged on every dollar, premium included: use
 *     `cashCentsOf` and `premiumDollarsOf` there.
 *
 * For a book without premiums every row is returned as the same object and
 * every accessor reads exactly what the page always read.
 */
import { insurancePremiumCents, premiumShareOfCashCents, withoutInsurancePremium } from '../../server/insurance-finance.js';

/**
 * @param {object[]} flights            booking rows as /api/admin/all-bookings returns them
 * @param {Record<string, number>} paidCentsById  cash collected per booking id (bookings-paid-amounts)
 */
export function buildInsuranceBasis(flights, paidCentsById = {}) {
  const rawById = new Map();
  for (const f of flights || []) {
    if (f?.id != null && insurancePremiumCents(f) > 0) rawById.set(f.id, f);
  }
  const travelFlights = (flights || []).map((f) => (rawById.has(f?.id) ? withoutInsurancePremium(f) : f));
  const premiumCentsOf = (flight) => {
    const raw = rawById.get(flight?.id);
    return raw ? insurancePremiumCents(raw) : 0;
  };
  /** Every dollar collected on the booking, premium included. */
  const cashCentsOf = (flight) => Number(paidCentsById?.[flight?.id]) || 0;
  /** Collected toward the travel: the premium's share of the cash taken out. */
  const travelCashCentsOf = (flight) => {
    const cash = cashCentsOf(flight);
    const raw = rawById.get(flight?.id);
    if (!raw) return cash;
    return Math.max(0, cash - premiumShareOfCashCents({ row: raw, paidCents: cash }));
  };
  return {
    travelFlights,
    hasInsurance: rawById.size > 0,
    premiumCentsOf,
    premiumDollarsOf: (flight) => premiumCentsOf(flight) / 100,
    cashCentsOf,
    travelCashCentsOf,
  };
}
